'use client';

import { Slide, styled } from '@mui/material';
import classnames from 'classnames';
import { usePathname } from 'next/navigation';
import { useEffect, useState } from 'react';

import { PanelsMap, type PanelTypes } from '@/components/panels/panelsMap';
import { useWorkspace } from '@/contexts/useWorkspace';
import { PanelSize } from '@/contexts/workspaceContext';

export const LeftSidePanel = () => {
  const { leftPanel } = useWorkspace();

  return (
    <SidePanel
      className='shrink-0 overflow-hidden'
      isOpen={leftPanel.isOpen}
      setIsOpen={leftPanel.setIsOpen}
      Component={leftPanel.Component}
      position='left'
      width='388px'
    />
  );
};

function getPanelWidth(panelSize: PanelSize | string): string {
  let width = '840px';
  if (panelSize === PanelSize.Small) {
    width = '640px';
  } else if (panelSize === PanelSize.Large) {
    width = '840px';
  } else if (panelSize === PanelSize.Full) {
    width = 'calc(100vw - 59px)';
  } else {
    width = panelSize;
  }
  return width;
}

export const RightPanelSpacer = () => {
  const { rightPanel } = useWorkspace();

  if (!rightPanel.isOpen || rightPanel.isAbsolute) {
    return null;
  }

  const width = getPanelWidth(rightPanel.panelSize);

  return <div className='shrink-0' style={{ width }} />;
};

export const RightSidePanel = () => {
  const { rightPanel } = useWorkspace();

  const width = getPanelWidth(rightPanel.panelSize);

  return (
    <SidePanel
      sx={{
        backgroundColor: 'var(--mui-palette-background-paper)',
        boxShadow: 'var(--mui-customShadows-panelMd)',
        borderInlineStart:
          (rightPanel.panelSize as PanelSize) === PanelSize.Full
            ? '0'
            : '1px solid var(--mui-palette-divider)',
        borderInlineEnd:
          (rightPanel.panelSize as PanelSize) === PanelSize.Full
            ? '0'
            : '1px solid var(--mui-palette-divider)',
      }}
      className='right-0 border-e-0 fixed'
      isOpen={rightPanel.isOpen}
      setIsOpen={rightPanel.setIsOpen}
      Component={rightPanel.Component}
      position='right'
      width={width}
    />
  );
};

const InternalSidePanel = ({
  className,
  isOpen,
  setIsOpen,
  Component,
  position,
  width,
}: {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  Component: PanelTypes | undefined;
  className: string;
  position: 'right' | 'left';
  width: string;
}) => {
  const path = usePathname();
  const [shouldAnimateExit, setShouldAnimateExit] = useState(true);

  useEffect(() => {
    if (isOpen) {
      setShouldAnimateExit(false);
      setIsOpen(false);
    }
    // don't want to be depend on isOpen, but only when the path changes, to check if the panel was open,
    // than don't animate the exit before setting the panel to be closed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path]);

  useEffect(() => {
    if (isOpen && !shouldAnimateExit) {
      setShouldAnimateExit(true);
    }
  }, [isOpen, shouldAnimateExit]);

  const Panel = Component?.panel && PanelsMap[Component.panel];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const props = Component?.props || ({} as any);
  const show = !!(isOpen && Panel);

  const transitionDuration = shouldAnimateExit ? 500 : 0;

  // Left panel uses width animation to push content
  if (position === 'left') {
    return (
      <div
        data-testid='left-side-panel'
        className={classnames('h-dvh flex flex-col z-[5]', className)}
        style={{
          width: show ? width : '0px',
          transition: `width ${transitionDuration}ms ease-out`,
        }}
      >
        <div
          className='h-full flex flex-col'
          style={{ minWidth: width, width }}
        >
          {show && <Panel {...props} />}
        </div>
      </div>
    );
  }

  // Right panel uses Slide animation (fixed position, overlays content)
  return (
    <Slide
      direction='left'
      in={show}
      mountOnEnter
      unmountOnExit
      timeout={{ enter: transitionDuration, exit: transitionDuration }}
      easing={{
        enter: 'ease-out',
        exit: 'cubic-bezier(0.4, 0, 0.2, 1)',
      }}
    >
      <div
        data-testid='right-side-panel'
        className={classnames('h-dvh flex flex-col z-[5] top-0', className)}
        style={{ width }}
      >
        {show && <Panel {...props} />}
      </div>
    </Slide>
  );
};

const SidePanel = styled(InternalSidePanel)({});
